import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import {
  invoiceReplacementAPI as api,
  ReplacementBatchView,
  ReplacementReviewView,
  replacementMoney,
} from '../../services/invoiceReplacementAPI';

const button =
  'rounded-md border border-outline px-3 py-2 text-sm disabled:opacity-50';
// Display separate states from the parent service's durable checkpoints.
function invoiceStates(state: string) {
  const originals: Record<string, string> = {
    planned: 'Open',
    draft_pending: 'Open',
    draft_ready: 'Open',
    void_pending: 'Void outcome pending',
    original_void: 'Void',
    finalize_pending: 'Void',
    ready: 'Void',
    paid: 'Void',
    canceled: 'Open',
    cancelled: 'Open',
  };
  const replacements: Record<string, string> = {
    planned: 'Planned',
    draft_pending: 'Draft outcome pending',
    draft_ready: 'Draft (nonpayable)',
    void_pending: 'Draft (nonpayable)',
    original_void: 'Draft (nonpayable)',
    finalize_pending: 'Finalization outcome pending',
    ready: 'Open',
    paid: 'Paid',
    canceled: 'Draft discarded',
    cancelled: 'Draft discarded',
  };
  return {
    original: originals[state] || 'Read receipt',
    replacement: replacements[state] || 'Read receipt',
  };
}
const panel =
  'rounded-lg border border-outline bg-surface-container-lowest p-4 space-y-3';

export default function AdminInvoiceReplacements() {
  const [params, setParams] = useSearchParams();
  const ids = (params.get('invoiceIds') || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  const batchId = params.get('batch');
  const [reason, setReason] = useState('');
  const [review, setReview] = useState<ReplacementReviewView | null>(null);
  const [batches, setBatches] = useState<ReplacementBatchView[]>([]);
  const [batch, setBatch] = useState<ReplacementBatchView | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [unknown, setUnknown] = useState(false);
  const [message, setMessage] = useState('');
  const [cutover, setCutover] = useState(false);
  const [recoverConfirmed, setRecoverConfirmed] = useState(false);
  const [cancelConfirmed, setCancelConfirmed] = useState(false);
  const [links, setLinks] = useState<
    Record<string, { url: string; expiresAt: string }>
  >({});

  useEffect(() => {
    let active = true;
    setBatch(null);
    setReview(null);
    setCutover(false);
    setRecoverConfirmed(false);
    setCancelConfirmed(false);
    setLinks({});
    Promise.all([
      api.list(),
      batchId ? api.get(batchId) : Promise.resolve(null),
    ])
      .then(([list, selected]) => {
        if (active) {
          setBatches(list.batches);
          setBatch(selected);
        }
      })
      .catch(() => {
        if (active)
          setMessage(
            'Could not read replacement status. Use Read status before continuing.'
          );
      });
    return () => {
      active = false;
    };
  }, [batchId]);

  async function run(action: () => Promise<void>, writes = false) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setMessage('');
    try {
      await action();
    } catch {
      if (writes) {
        setUnknown(true);
        setMessage(
          'Outcome unknown. Do not repeat the action. Read status and inspect the durable batch receipt before recovering. No automatic retry was made.'
        );
      } else
        setMessage(
          'Could not read replacement status. Check your access and use Read status.'
        );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function readStatus() {
    const list = await api.list();
    setBatches(list.batches);
    if (batchId) setBatch(await api.get(batchId));
    setUnknown(false);
    setCutover(false);
    setRecoverConfirmed(false);
    setCancelConfirmed(false);
    setMessage(
      'Status refreshed. Inspect the batch receipt before choosing the next action.'
    );
  }
  function selectBatch(next: ReplacementBatchView) {
    setBatch(next);
    setReview(null);
    setCutover(false);
    setRecoverConfirmed(false);
    setCancelConfirmed(false);
    setBatches((previous) => [
      next,
      ...previous.filter((b) => b.id !== next.id),
    ]);
    if (next.state === 'blocked') {
      setUnknown(true);
      setMessage(
        'Batch outcome requires inspection. Read status before recovering this existing batch. No automatic retry was made.'
      );
    }
    setParams({ batch: next.id });
  }
  const prepared = batch?.state === 'prepared';
  const terminal =
    !!batch && ['canceled', 'cancelled', 'completed'].includes(batch.state);
  const canCancel =
    !!batch &&
    !terminal &&
    batch.items.length > 0 &&
    batch.items.every((item) =>
      ['planned', 'draft_pending', 'draft_ready'].includes(item.state)
    );

  return (
    <PageLayout>
      <div className="space-y-5 text-on-surface">
        <header>
          <h1 className="text-2xl font-semibold">Invoice replacements</h1>
          <p>
            Review historical invoices, prepare nonpayable drafts, then
            explicitly cut over originals.
          </p>
          <Link to="/admin/invoices" className="text-primary underline">
            Back to invoices
          </Link>
        </header>
        <div role="status" aria-live="polite" className="min-h-[1.5rem]">
          {busy ? 'Working…' : message}
        </div>
        <button
          className={button}
          disabled={busy}
          onClick={() => run(readStatus)}
        >
          Read status
        </button>
        <section className={panel} aria-labelledby="replacement-review-title">
          <h2 id="replacement-review-title" className="text-lg font-semibold">
            Review selected originals
          </h2>
          <p>
            {ids.length
              ? `${ids.length} selected invoice(s): ${ids.join(', ')}`
              : 'Select open invoices on the invoices page to begin a review.'}
          </p>
          <label className="block">
            Reason
            <input
              className="block w-full rounded border border-outline bg-surface p-2"
              value={reason}
              disabled={busy}
              onChange={(e) => {
                setReason(e.target.value);
                setReview(null);
              }}
            />
          </label>
          <button
            className={button}
            disabled={busy || unknown || !ids.length || !reason.trim()}
            onClick={() =>
              run(async () => {
                setReview(null);
                setReview(await api.review(ids, reason.trim()));
              })
            }
          >
            Review (no writes)
          </button>
          {review && (
            <div className="space-y-3">
              <p>
                Saved card: {review.card.brand} ending {review.card.last4}.
                Customer confirmation is required separately.
              </p>
              <ul>
                {review.invoices.map((invoice) => (
                  <li key={invoice.id} className="py-2">
                    <strong>{invoice.number}</strong> —{' '}
                    {replacementMoney(invoice.amountDue, review.currency)} —
                    original awaiting review
                    {invoice.storedPaymentUrl && (
                      <a
                        className="ml-3 underline"
                        href={invoice.storedPaymentUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Stored original link
                      </a>
                    )}
                    {invoice.currentPaymentUrl && (
                      <a
                        className="ml-3 underline"
                        href={invoice.currentPaymentUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Current original link
                      </a>
                    )}
                  </li>
                ))}
              </ul>
              <p className="font-semibold">
                Total: {replacementMoney(review.total, review.currency)}
              </p>
              {review.warnings.map((warning, index) => (
                <p key={index}>{warning}</p>
              ))}
              <p>
                Review expires {new Date(review.expiresAt).toLocaleString()}.
                Preparation creates nonpayable drafts; it does not void
                originals or take payment.
              </p>
              <button
                className={button}
                disabled={
                  busy || unknown || Date.parse(review.expiresAt) <= Date.now()
                }
                onClick={() =>
                  run(async () => {
                    const token = review.reviewToken;
                    setReview(null);
                    selectBatch(await api.prepare(token));
                  }, true)
                }
              >
                Prepare drafts (no void/pay)
              </button>
            </div>
          )}
        </section>
        <section className={panel} aria-labelledby="replacement-recent-title">
          <h2 id="replacement-recent-title" className="text-lg font-semibold">
            Recent replacement batches
          </h2>
          <p>Reopen a durable batch after a refresh or interrupted request.</p>
          {!batches.length && <p>No batches loaded.</p>}
          <ul>
            {batches.map((row) => (
              <li key={row.id}>
                <Link
                  className="text-primary underline"
                  to={`?batch=${encodeURIComponent(row.id)}`}
                  aria-disabled={busy}
                  onClick={(event) => {
                    if (busy) event.preventDefault();
                  }}
                >
                  {row.id} — {row.state} —{' '}
                  {replacementMoney(row.total, row.currency)}
                </Link>
              </li>
            ))}
          </ul>
        </section>
        {batch && (
          <section className={panel} aria-labelledby="replacement-batch-title">
            <h2 id="replacement-batch-title" className="text-lg font-semibold">
              Batch {batch.id}
            </h2>
            <p>
              State: {batch.state} · Reason: {batch.reason}
            </p>
            <p className="font-semibold">
              Total: {replacementMoney(batch.total, batch.currency)}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <caption className="sr-only">
                  Original and replacement invoice states
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Original</th>
                    <th scope="col">Replacement</th>
                    <th scope="col">Amount</th>
                    <th scope="col">State / payment</th>
                    <th scope="col">History / collection</th>
                  </tr>
                </thead>
                <tbody>
                  {batch.items.map((item) => (
                    <tr key={item.id}>
                      <td className="p-2">
                        {item.originalNumber}
                        <p>{invoiceStates(item.state).original}</p>
                        <br />
                        <small>{item.originalId}</small>
                      </td>
                      <td className="p-2">
                        {item.replacementNumber}
                        <p>{invoiceStates(item.state).replacement}</p>
                        <br />
                        <small>{item.replacementId}</small>
                      </td>
                      <td className="p-2">
                        {replacementMoney(item.amount, batch.currency)}
                      </td>
                      <td className="p-2">
                        {item.state} /{' '}
                        {item.paymentStatus || 'No payment receipt'}
                        {item.error && (
                          <p>Requires inspection before recovery.</p>
                        )}
                      </td>
                      <td className="p-2 space-y-2">
                        <button
                          className={button}
                          disabled={busy || !item.pdfArchived}
                          onClick={() =>
                            run(async () => {
                              const blob = await api.historyPdf(
                                batch.id,
                                item.id
                              );
                              const url = URL.createObjectURL(blob);
                              const anchor = document.createElement('a');
                              anchor.href = url;
                              anchor.download = 'archived-invoice.pdf';
                              anchor.click();
                              setTimeout(() => URL.revokeObjectURL(url), 1000);
                            })
                          }
                        >
                          Archived PDF
                        </button>
                        <button
                          className={button}
                          disabled={
                            busy ||
                            unknown ||
                            item.state !== 'ready' ||
                            !!item.paymentStatus
                          }
                          onClick={() =>
                            run(async () => {
                              const link = await api.collectionLink(
                                batch.id,
                                item.id
                              );
                              setLinks((previous) => ({
                                ...previous,
                                [item.id]: link,
                              }));
                            }, true)
                          }
                        >
                          Create customer payment link (no email)
                        </button>
                        {links[item.id] && (
                          <p>
                            <a
                              className="underline"
                              href={links[item.id].url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Customer payment link
                            </a>
                            <button
                              className={button}
                              disabled={busy}
                              onClick={() =>
                                run(async () => {
                                  await navigator.clipboard.writeText(
                                    links[item.id].url
                                  );
                                  setMessage(
                                    'Customer payment link copied. No email was sent.'
                                  );
                                })
                              }
                            >
                              Copy customer payment link
                            </button>{' '}
                            · expires{' '}
                            {new Date(
                              links[item.id].expiresAt
                            ).toLocaleString()}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <label className="block">
              <input
                type="checkbox"
                checked={cutover}
                disabled={busy || unknown || !prepared}
                onChange={(e) => setCutover(e.target.checked)}
              />{' '}
              I approve voiding these originals and finalizing replacements. Old
              payment links will change and stop accepting payment.
            </label>
            <button
              className={button}
              disabled={busy || unknown || !prepared || !cutover}
              onClick={() =>
                run(async () => {
                  setCutover(false);
                  selectBatch(await api.execute(batch.id, batch.fingerprint));
                }, true)
              }
            >
              Cut over originals
            </button>
            <label className="block">
              <input
                type="checkbox"
                checked={recoverConfirmed}
                disabled={busy || unknown || terminal}
                onChange={(e) => setRecoverConfirmed(e.target.checked)}
              />{' '}
              Check provider receipts and resume this existing intent. Do not
              create a fresh generation.
            </label>
            <button
              className={button}
              disabled={busy || unknown || terminal || !recoverConfirmed}
              onClick={() =>
                run(async () => {
                  setRecoverConfirmed(false);
                  setCancelConfirmed(false);
                  selectBatch(await api.recover(batch.id, batch.fingerprint));
                }, true)
              }
            >
              Recover (check receipt/resume)
            </button>
            <label className="block">
              <input
                type="checkbox"
                checked={cancelConfirmed}
                disabled={busy || unknown || !canCancel}
                onChange={(event) => setCancelConfirmed(event.target.checked)}
              />{' '}
              Discard prepared drafts and release the originals. Cancel only
              before any original is voided; retain the audit history.
            </label>
            <button
              className={button}
              disabled={busy || unknown || !canCancel || !cancelConfirmed}
              onClick={() =>
                run(async () => {
                  setCancelConfirmed(false);
                  selectBatch(await api.cancel(batch.id, batch.fingerprint));
                }, true)
              }
            >
              Cancel preparation
            </button>
          </section>
        )}
      </div>
    </PageLayout>
  );
}
